import { Inject, Injectable } from '@angular/core';
import { UserManager, User, WebStorageStateStore } from 'oidc-client-ts';
import { Environment, ENVIRONMENT } from '@classifieds-sell/environment';

@Injectable()
export class AuthService {
    private static _userManager: UserManager;

    public get userManager() {
        return AuthService._userManager;
    }

    constructor(@Inject(ENVIRONMENT) private env: Environment) {
        if (!AuthService._userManager) {
            /* eslint-disable @typescript-eslint/naming-convention */
            AuthService._userManager = new UserManager({
                client_id: this.env.authClientId,
                redirect_uri: `${this.env.frontendUrl}/sign-in.html`,
                authority: `${this.env.authUrl}`,
                response_type: 'code',
                scope: this.env.authScope,
                loadUserInfo: true,
                silent_redirect_uri: `${this.env.frontendUrl}/sign-in-silent.html`,
                automaticSilentRenew: true,
                revokeTokensOnSignout: true,
                userStore: new WebStorageStateStore({
                    store: window.sessionStorage
                }),
                stateStore: new WebStorageStateStore({
                    store: window.sessionStorage
                })
            });
            /* eslint-enable @typescript-eslint/naming-convention */
        }
    }

    public async ensureLoggedIn(returnUrl: string) {
        const user = await this.userManager.getUser();
        if (user == null || user.expired) {
            await this.userManager.clearStaleState();
            await this.startSignin(returnUrl);
        }
    }

    public async isLoggedIn(): Promise<boolean> {
        return this.getLoggedInUser() != null;
    }

    public async getLoggedInUser(): Promise<User> {
        const user = await this.userManager.getUser();
        return user == null || user.expired ? null : user;
    }

    public async authToken(): Promise<string> {
        const authToken = await this.getAuthToken();
        return authToken;
    }

    public signinSilentCallback(args?: any) {
        return this.userManager.signinSilentCallback(args);
    }

    public signinRedirectCallback(args?: any) {
        return this.userManager.signinRedirectCallback(args);
    }

    private async startSignin(returnUrl: string) {
        return this.userManager.signinRedirect({
            state: returnUrl
        });
    }

    private async renew() {
        try {
            const user = await this.userManager.signinSilent();
            if (user == null) {
                throw 'No user token found ';
            }
        } catch (err) {
            await this.startSignin(window.location.href);
        }
    }

    private async getAuthToken(): Promise<any> {
        const user = await this.userManager.getUser();
        if (user && !user.expired) {
            return `Bearer ${user.access_token}`;
        }

        await this.renew();

        return await this.getAuthToken();
    }
}
